import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api/client";
import type { CatalogCollection, PriceQuote, TopProfile } from "../lib/api/types";
import { tenantPath, useCatalogueMode, useTenantSlug } from "../lib/tenant";
import { ALL_DOORS, useVisualizer, type DoorObject, type DoorSelection } from "../lib/state/visualizerStore";
import { legendRows, effectiveSize, isConfigured, sharedSelection, blendScopeState } from "../lib/state/selectors";
import { sizeDims, type DoorSize } from "../lib/geometry/doorSize";
import { useRenderComposite } from "../lib/render/useRenderComposite";
import { AppShell, card } from "../components/AppShell";
import { Button } from "../components/Button";
import { BlendWait } from "../components/BlendWait";
import { SparkleIcon, ArrowRightIcon, CloseIcon, PlusIcon, CheckIcon } from "../components/icons";
import { QuadEditor } from "../components/QuadEditor";
import { FullscreenEditor } from "../components/FullscreenEditor";
import { CompareView } from "../components/CompareView";
import { DoorPicker } from "../components/DoorPicker";
import { DoorLegend } from "../components/DoorLegend";
import { DoorGeometryPanel } from "../components/DoorGeometryPanel";
import { ManufacturerSelect } from "../components/ManufacturerSelect";
import { Thumb } from "../components/Thumb";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PriceLine, priceSummary } from "../components/PriceLine";
import { dollars, dollarsCents } from "../lib/format";
import { RecommendedRail, selectionKey } from "../components/RecommendedRail";
import { useBranding } from "../lib/state/brandingContext";
import type { PostcardPreset } from "../lib/api/types";

const EMPTY_SELECTION: DoorSelection = { designId: null, colorId: null, windowOptionId: null, glassTypeId: null, hardwareId: null, constructionId: null };

// Seeds for the "Top shape" override buttons — starting points for the same state the on-photo
// profile handles manipulate (the customer drags the handles from here; Flat clears the profile).
const CHAMFER_SEED: TopProfile = { kind: "chamfer", cutTopLeft: 0.12, cutSideLeft: 0.12, cutTopRight: 0.12, cutSideRight: 0.12, rise: 0 };
const ARC_SEED: TopProfile = { kind: "arc", cutTopLeft: 0, cutSideLeft: 0, cutTopRight: 0, cutSideRight: 0, rise: 0.1 };

// Plainest White finish (shortest name wins: "White" over "Bone White"/"White PVDF"). Used to give a
// panel pick an immediate preview before the customer has chosen a colour.
function defaultColorId(colors: { id: string; name: string; code: string }[]): string | null {
  const whites = colors.filter((c) => /white/i.test(c.name) || /white/i.test(c.code));
  if (whites.length === 0) return null;
  return [...whites].sort((a, b) => a.name.length - b.name.length)[0].id;
}

export function VisualizePage() {
  const tenantSlug = useTenantSlug();
  const navigate = useNavigate();

  const doc = useVisualizer((s) => s.document);
  const ui = useVisualizer((s) => s.ui);
  const render = useVisualizer((s) => s.render);
  const catalog = useVisualizer((s) => s.catalog);

  const setCollection = useVisualizer((s) => s.setCollection);
  const applyPresetToDoc = useVisualizer((s) => s.applyPreset);
  const updateDoorSelection = useVisualizer((s) => s.updateDoorSelection);
  const updateAllDoorSelections = useVisualizer((s) => s.updateAllDoorSelections);
  const setDoorQuad = useVisualizer((s) => s.setDoorQuad);
  const setDoorTopProfile = useVisualizer((s) => s.setDoorTopProfile);
  const setDoorSizeOverride = useVisualizer((s) => s.setDoorSizeOverride);
  const addDoor = useVisualizer((s) => s.addDoor);
  const removeDoor = useVisualizer((s) => s.removeDoor);
  const selectDoor = useVisualizer((s) => s.selectDoor);
  const setMode = useVisualizer((s) => s.setMode);
  const setOpenSection = useVisualizer((s) => s.setOpenSection);
  const setFullscreen = useVisualizer((s) => s.setFullscreen);
  const setComparePosition = useVisualizer((s) => s.setComparePosition);
  const setCatalog = useVisualizer((s) => s.setCatalog);

  const { renderComposite, aiBlend } = useRenderComposite(tenantSlug);
  const { branding } = useBranding();
  // Server-authoritative gates (branding flags): the demo/manufacturer scopes are unpriced and
  // lead-less. Null branding (loading / transient error) keeps the full tenant experience.
  const pricingEnabled = branding?.pricingEnabled ?? true;
  const leadCaptureEnabled = branding?.leadCaptureEnabled ?? true;
  // Showroom (full active catalogue) → ask the server for every enabled manufacturer/collection.
  const catalogueMode = useCatalogueMode();

  // Transient pointer state: which "Your doors" row is hovered, so the matching door glows on the photo.
  const [hoveredDoorId, setHoveredDoorId] = useState<string | null>(null);
  // Door pending removal — drives the confirm modal (null = closed).
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  // Manufacturer chosen on the initial "Choose a manufacturer → collection" card (multi-brand demo);
  // "" falls back to the first manufacturer so its collections show immediately.
  const [initialManufId, setInitialManufId] = useState<string>("");

  function handleAddDoor() {
    const id = addDoor();
    if (!id) return;
    // Focus the new door individually (even from ALL mode) — it lands as an unfitted centred quad
    // the customer must drag into place, so their attention belongs on that one door first.
    selectDoor(id);
    setMode("edit");
    setOpenSection("design");
  }

  function confirmRemove() {
    if (!pendingRemoveId) return;
    removeDoor(pendingRemoveId);
    setPendingRemoveId(null);
    void renderComposite();
  }

  // Load the catalogue; on a persisted reload, restore the active collection + re-derive the composite.
  useEffect(() => {
    if (!doc.photoUrl) {
      navigate(tenantPath(tenantSlug));
      return;
    }
    api
      .getCollections(tenantSlug, { full: catalogueMode })
      .then((cols) => {
        setCatalog({ collections: cols });
        const st = useVisualizer.getState();
        const docId = st.document.collectionId;
        if (!docId && cols.length === 1) {
          setCatalog({ collection: cols[0] });
          st.setCollection(cols[0].id);
        } else if (docId) {
          const c = cols.find((x) => x.id === docId);
          if (c) setCatalog({ collection: c });
          if (st.document.doorOrder.some((id) => st.document.doors[id]?.selection.colorId)) void renderComposite();
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug, catalogueMode]);

  // Escape closes the fullscreen lightbox.
  useEffect(() => {
    if (!ui.fullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ui.fullscreen, setFullscreen]);

  const doors = doc.doorOrder.map((id) => doc.doors[id]).filter(Boolean);
  // ALL-doors mode (the multi-door default): style picks fan out to every door; geometry stays
  // per-door. `selected` is the door being edited in INDIVIDUAL mode only (null in ALL mode —
  // doc.doors[ALL_DOORS] can never exist, but be explicit rather than lean on that).
  const allMode = ui.selectedDoorId === ALL_DOORS;
  const selected = !allMode && ui.selectedDoorId ? doc.doors[ui.selectedDoorId] ?? null : null;

  // Price the door being edited against the dealer's rules whenever the selection (or its size)
  // changes. ALL mode shows the FIRST door's price as the representative line (doors share the
  // style but may differ in size); the quote page keeps per-door prices + the combined total.
  const [price, setPrice] = useState<PriceQuote | null>(null);
  const selForPriceDoor = allMode ? doors[0] : selected ?? undefined;
  const selForPrice = selForPriceDoor?.selection;
  const selectedSize: DoorSize = selForPriceDoor ? effectiveSize(selForPriceDoor) : "double";
  const selectedIsSingle = selectedSize === "single";
  const priceCollectionId = catalog.collection?.id ?? null;
  useEffect(() => {
    if (!priceCollectionId || !pricingEnabled) { setPrice(null); return; }
    const coll = catalog.collection;
    const color = coll?.colors.find((c) => c.id === selForPrice?.colorId);
    const win = coll?.windows.find((w) => w.id === selForPrice?.windowOptionId);
    let cancelled = false;
    api
      .price(tenantSlug, {
        collectionId: priceCollectionId,
        designId: selForPrice?.designId ?? undefined,
        colorId: selForPrice?.colorId ?? undefined,
        colorGroup: color?.family ?? undefined,
        windowOptionId: selForPrice?.windowOptionId ?? undefined,
        windowGroup: win?.placement ?? undefined,
        hardwareId: selForPrice?.hardwareId ?? undefined,
        constructionId: selForPrice?.constructionId ?? undefined,
        isSingle: selectedIsSingle,
      })
      .then((q) => { if (!cancelled) setPrice(q); })
      .catch(() => { if (!cancelled) setPrice(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug, pricingEnabled, priceCollectionId, selForPrice?.designId, selForPrice?.colorId, selForPrice?.windowOptionId, selForPrice?.hardwareId, selForPrice?.constructionId, selectedIsSingle]);

  // Door name shown in the price box: collection + chosen panel, e.g. "Lincoln Short Panel" (no "Amarr").
  // Short/Long-panel designs carry a panel length (SP/LP) but are named just "Short"/"Long", so append
  // "Panel"; named designs (e.g. Carriage Court "Ashley") are left as-is.
  const priceDesign = catalog.collection?.designs.find((d) => d.id === selForPrice?.designId);
  const panelLabel =
    priceDesign && (priceDesign.panelLength === "SP" || priceDesign.panelLength === "LP") && !/panel/i.test(priceDesign.name)
      ? `${priceDesign.name} Panel`
      : priceDesign?.name;
  const doorName =
    [catalog.collection?.name, panelLabel].filter(Boolean).join(" ") ||
    catalog.collection?.name ||
    "";

  if (!doc.photoUrl) return null;

  // Collections grouped by brand, in catalogue order (the demo scope spans manufacturers; a normal
  // dealer catalogue usually yields a single group and the header row is skipped).
  const brandGroups: { id: string; name: string; collections: CatalogCollection[] }[] = [];
  for (const c of catalog.collections) {
    const id = c.manufacturer?.id ?? "";
    let g = brandGroups.find((x) => x.id === id);
    if (!g) {
      g = { id, name: c.manufacturer?.name ?? "Other", collections: [] };
      brandGroups.push(g);
    }
    g.collections.push(c);
  }
  // Manufacturer-first: on a multi-brand catalogue the initial card leads with a manufacturer selector
  // and only that brand's collections appear below (default = first brand). Single-brand → all collections.
  const multiBrand = brandGroups.length > 1;
  const activeInitialManuf = initialManufId || brandGroups[0]?.id || "";
  const shownInitialCollections = multiBrand
    ? brandGroups.find((g) => g.id === activeInitialManuf)?.collections ?? []
    : catalog.collections;

  const composite = render.compositeUrl;
  // Full-res display: the canonical image (fullscreen editing/lightbox always show this)…
  const display = render.integratedUrl ?? composite ?? doc.photoUrl;
  // …and its bandwidth-friendly preview variant for the in-page imgs. Each layer falls back to its
  // full-res URL when the server sent no preview (older server, preview encode failure).
  const displayPreview = render.integratedUrl
    ? render.integratedPreviewUrl ?? render.integratedUrl
    : composite
      ? render.compositePreviewUrl ?? composite
      : doc.photoUrl;
  // Edit-mode backdrop follows the latest result (incl. AI blend) so a blend is visible while editing.
  const backdrop = displayPreview ?? doc.photoUrl;
  const hasRender = !!composite;
  const busy = render.status === "rendering" || render.status === "blending";
  const blending = render.status === "blending";
  // Gate for "Make it photorealistic": ALL mode needs at least one configured door (the blend
  // chains through every configured one); individual mode needs the selected door configured.
  const selectedConfigured = allMode ? doors.some(isConfigured) : !!(selected && isConfigured(selected));
  // Per-door blend state over the doors in SCOPE: the selected door individually, or every
  // CONFIGURED door in ALL mode (an unconfigured door can't blend, so it must not block "ready").
  // This is what fixes the app-global bug: switching to an unblended door re-shows the button
  // instead of the other door's "ready" state.
  const scopeDoors = (allMode ? doors : selected ? [selected] : []).filter(isConfigured);
  const blendScope = blendScopeState(scopeDoors, render.blends, doc.collectionId);
  // "Door 2 used the standard render — try again." — a door the server degraded to the geometric
  // composite keeps the button visible; re-clicking re-blends ONLY the non-fresh doors (aiBlend
  // skips blended-fresh ones), which is the per-door retry.
  const fallbackNames = blendScope.fallbackDoors.map((d) => `Door ${d.index + 1}`);
  const fallbackHint =
    fallbackNames.length === 0
      ? null
      : `${fallbackNames.length === 1 ? fallbackNames[0] : `${fallbackNames.slice(0, -1).join(", ")} and ${fallbackNames[fallbackNames.length - 1]}`} used the standard render — try again.`;
  const effectiveMode = hasRender ? ui.mode : "edit";
  const rows = legendRows({ document: doc, render, catalog, ui });
  // Shared by the inline QuadEditor and the fullscreen editing surface — same doors, same handle
  // scope, so editing in either place is the same interaction on the same state.
  const editorDoors = doors.map((d) => ({ id: d.id, quad: d.quad, topProfile: d.topProfile ?? null, occlusionSvgPath: d.detected.occlusionSvgPath ?? null }));
  const activeDoorIds = allMode ? doors.map((d) => d.id) : ui.selectedDoorId ? [ui.selectedDoorId] : [];
  // Low-confidence detection nudge: the server graded its own quad/profile fit poorly, so ask the
  // homeowner to double-check the corners. 0 = unknown (manual/pre-feature doors) — never nudge
  // those. ALL mode nudges when ANY door meets the condition (all corners are live to fix).
  const lowFitOf = (d: DoorObject | null | undefined) => {
    const f = d?.detected.fitConfidence ?? 0;
    return f > 0 && f < 0.5;
  };
  const lowFit = allMode ? doors.some(lowFitOf) : lowFitOf(selected);

  // Size context only — never the opposite-size figure (a double's card must not show the single price).
  const priceSubtext = price ? (selectedIsSingle ? "8×7 installed" : "16×7 installed") : "";
  // Compact figures for the mobile sticky quote bar; null when no price resolves for this size
  // (the bar then shows the door name / "Pricing on request" instead of a bogus $0).
  const barSummary = price ? priceSummary(price, selectedIsSingle) : null;
  const barPrice = barSummary && barSummary.headlineCents > 0 ? barSummary : null;

  function pickCollection(c: CatalogCollection) {
    setCatalog({ collection: c });
    setCollection(c.id); // wipes door selections; re-add a default below so the door previews immediately
    // Auto-select a default panel + colour so the door renders the moment a collection is chosen
    // (plainest White, falling back to the first colour). Restrict the default to options available
    // at EVERY target door's size — ALL mode targets every door (a single+double pair must not
    // default to a double-only panel that blanks the single); individual mode just the selected one.
    const targets = allMode ? doors : selected ? [selected] : [];
    if (targets.length === 0) return;
    const widths = [...new Set(targets.map((d) => sizeDims(effectiveSize(d)).width))];
    const fits = (w?: number[]) => !w || w.length === 0 || widths.every((x) => w.includes(x));
    const firstDesign = c.designs.find((d) => fits(d.availableWidthsFt)) ?? c.designs[0];
    if (!firstDesign) return;
    const colorPool = c.colors.filter((col) => fits(col.availableWidthsFt));
    const colorId = defaultColorId(colorPool) ?? colorPool[0]?.id ?? c.colors[0]?.id ?? null;
    const patch = { designId: firstDesign.id, colorId, windowOptionId: null, glassTypeId: null };
    if (allMode) updateAllDoorSelections(patch);
    else updateDoorSelection(targets[0].id, patch);
    void renderComposite();
  }

  function applySelection(patch: Partial<DoorSelection>) {
    let next = patch;
    // Picking a panel before any colour: default to White so the door previews right away (the
    // colour picker then shows White selected and the customer can change it). In ALL mode only
    // when NO door has a colour yet (the normal state after a collection switch) — if some door
    // already carries a colour, a panel pick must not stomp it.
    const noColorYet = allMode ? !doors.some((d) => d.selection.colorId) : !selected?.selection.colorId;
    if (patch.designId && !patch.colorId && noColorYet) {
      const white = defaultColorId(catalog.collection?.colors ?? []);
      if (white) next = { ...patch, colorId: white };
    }
    if (allMode) updateAllDoorSelections(next);
    else if (ui.selectedDoorId) updateDoorSelection(ui.selectedDoorId, next);
    else return;
    void renderComposite();
  }

  // Postcard QR sessions: the "Recommended for your home" rail. Applying a preset switches the
  // collection AND re-applies its selection to every door (spec §6), then re-composites. Only
  // presets whose collection is in the loaded catalogue are offered — a collection the dealer
  // stopped offering after the card was mailed must not be tappable (its options/pricing would
  // desync from the applied selection). Until the catalogue loads the rail waits.
  const postcard = doc.postcard ?? null;
  const railPresets = postcard
    ? postcard.presets.filter((p) => catalog.collections.some((c) => c.id === p.selection.collectionId))
    : [];
  // ALL mode highlights a rail preset only when every door genuinely carries it (sharedSelection
  // nulls mixed fields, so a half-applied preset never reads as active).
  const activePresetKey = allMode
    ? selectionKey(doc.collectionId, sharedSelection(doors))
    : selected
      ? selectionKey(doc.collectionId, selected.selection)
      : null;
  function applyPreset(preset: PostcardPreset) {
    const c = catalog.collections.find((x) => x.id === preset.selection.collectionId);
    if (!c) return; // filtered rail should make this unreachable; never desync catalogue vs doc
    setCatalog({ collection: c });
    applyPresetToDoc(preset);
    void renderComposite();
  }

  // Manually correct a door's single/double size (null = revert to the detected verdict). Takes the
  // door id because ALL mode shows one size row PER DOOR — sizes never gang, only styles do.
  // If the new size makes that door's chosen panel unavailable, clear its selection so the picker
  // re-offers only size-valid options (mirrors how changing collection invalidates picks).
  function setSize(doorId: string, size: DoorSize) {
    const door = doc.doors[doorId];
    if (!door) return;
    setDoorSizeOverride(doorId, size);
    const widthFt = size === "single" ? 8 : 16;
    const design = catalog.collection?.designs.find((d) => d.id === door.selection.designId);
    const widths = design?.availableWidthsFt;
    if (design && widths && widths.length > 0 && !widths.includes(widthFt)) {
      updateDoorSelection(doorId, EMPTY_SELECTION);
    }
    void renderComposite();
  }

  // Reset/override for a door's top-edge profile — the on-photo handles fine-tune the same state,
  // so the buttons just seed a sensible starting shape (re-clicking the active shape re-seeds it,
  // doubling as a "reset my hand-tuned cuts" affordance). Per-door like setSize: ALL mode shows one
  // top-shape row per door. Mutate, then re-render (the profile participates in the render cache
  // key, so stale renders invalidate).
  function setTopShape(doorId: string, kind: TopProfile["kind"]) {
    if (!doc.doors[doorId]) return;
    setDoorTopProfile(doorId, kind === "flat" ? null : kind === "chamfer" ? CHAMFER_SEED : ARC_SEED);
    void renderComposite();
  }

  return (
    <AppShell step="visualize">
      {postcard && (
        <RecommendedRail
          salutation={postcard.salutation}
          presets={railPresets}
          activeKey={activePresetKey}
          onApply={applyPreset}
        />
      )}
      <div className="rg-visualize-grid">
        <div className="rg-visualize-col">
          {/* Padding reads a CSS var so the mobile stylesheet can slim the sticky card's chrome —
              every pixel pinned here is a pixel the style picker below can't use. */}
          <div style={{ ...card, padding: "var(--rg-preview-pad, 16px)" }} className="rg-visualize-preview">
            {hasRender && (
              <div className="rg-mode-row" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <SegButton active={effectiveMode === "edit"} onClick={() => setMode("edit")}>Edit</SegButton>
                  <SegButton active={effectiveMode === "compare"} onClick={() => setMode("compare")}>Before / After</SegButton>
                </div>
                {/* Fit-adjust hint lives at the top-right, just above the image and out of the way of
                    the result below. Edit mode only — Before/After has no draggable corners. A low
                    server fit confidence swaps in a firmer nudge to actually check the corners.
                    NOT gated on `busy`: `compositeUrl` (hence `hasRender`) survives a re-render, so
                    this is the only element that would toggle when `busy` flips — unmounting it would
                    shift the image and jitter the scrolled page on every style click. Keep it mounted.
                    On mobile the GENERIC hint is hidden statically (rg-hide-m — a per-breakpoint rule
                    that never toggles with render state); the lowFit nudge is actionable and stays. */}
                {effectiveMode === "edit" && (
                  <div className={lowFit ? undefined : "rg-hide-m"} style={{ marginLeft: "auto", minWidth: 0, textAlign: "right", color: "var(--muted)", fontSize: 12, lineHeight: 1.35 }}>
                    {lowFit
                      ? allMode
                        ? "We weren't fully sure of every door's outline — double-check the corners on the photo."
                        : "We weren't fully sure of this door's outline — double-check the corners on the photo."
                      : "Not lined up? Drag the corners on the photo to adjust."}
                  </div>
                )}
              </div>
            )}

            {/* Neutral media wrapper: a plain block div in portrait (QuadEditor's overlay anchors to
                its own root, so this changes nothing) — in landscape-compact it becomes the left
                grid cell so the card's chrome can sit BESIDE the height-bound photo instead of
                stacking under it and wasting the wide axis. */}
            <div className="rg-preview-media">
            {effectiveMode === "compare" && displayPreview ? (
              <CompareView
                before={doc.photoUrl}
                after={displayPreview}
                position={ui.comparePosition}
                onPositionChange={setComparePosition}
                onFullscreen={() => setFullscreen(true)}
              />
            ) : (
              <QuadEditor
                photoUrl={doc.photoUrl}
                backdropUrl={backdrop}
                doors={editorDoors}
                // ALL mode: every door's handles are live at once (geometry edits still land on the
                // door being dragged only); individual mode: just the selected door — as before.
                activeDoorIds={activeDoorIds}
                hoveredDoorId={hoveredDoorId}
                busy={busy}
                onSelectDoor={selectDoor}
                onQuadChange={(id, q) => setDoorQuad(id, q)}
                onQuadCommit={(id, q) => { setDoorQuad(id, q); void renderComposite(); }}
                onProfileChange={(id, tp) => setDoorTopProfile(id, tp)}
                onProfileCommit={(id, tp) => { setDoorTopProfile(id, tp); void renderComposite(); }}
                onFullscreen={() => setFullscreen(true)}
              />
            )}
            </div>

            {render.status === "error" && render.error && (
              <div style={{ marginTop: 10, color: "var(--danger)", fontSize: 13 }}>{render.error}</div>
            )}
            {!hasRender && render.status !== "error" && (
              // Stays mounted while a render is in flight (don't gate on `busy`): unmounting the
              // caption mid-render collapses its height and shifts the scrolled page up, then back
              // down when it remounts — the reported click-to-render jitter.
              <div style={{ marginTop: 10, color: "var(--muted)", fontSize: 13 }}>
                {lowFit
                  ? allMode
                    ? "We couldn't line every door up precisely — check the corners match your openings, then pick a style."
                    : "We couldn't line this door up precisely — check the corners match your opening, then pick a style."
                  : "Drag the corners to fit the door opening, then pick a style."}
              </div>
            )}
            {/* Photorealistic-render CTA, placed right under the preview where the customer is looking, so it's
                clear the flat preview can be turned into a true-to-life render (was a buried "AI Blend" button).
                Shown in BOTH Edit and Before/After modes — the compare slider is exactly where a customer
                decides the flat composite isn't convincing enough, so the upgrade must be reachable there. */}
            {hasRender && (
              // Fixed-height slot: all three blend states (button / spinner banner / ready line)
              // center inside the same 48px, so starting or finishing a blend never resizes the
              // sticky card and reflows the scrolled page below it.
              <div style={{ marginTop: 12, minHeight: 48, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                {blending ? (
                  // Staged narration for the flow's longest wait (mounts fresh per blend, so the
                  // stages restart) — see BlendWait for why it's time-driven.
                  <BlendWait />
                ) : blendScope.ready ? (
                  // Ready is PER-SCOPE, not app-global: every door in scope is blended-fresh with a
                  // real AI blend (no fallback) — selecting a different, unblended door re-offers the button.
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--brand-active)", fontSize: 13, fontWeight: 600 }}>
                    <CheckIcon size={16} /> Photorealistic preview ready
                  </div>
                ) : (
                  <>
                    <Button variant="primary" block disabled={!selectedConfigured} onClick={() => void aiBlend()} leadingIcon={<SparkleIcon />}>
                      Make it photorealistic
                    </Button>
                    {/* Generic value-prop subtext is desktop garnish (hidden on mobile to slim the
                        sticky card); the fallbackHint retry message is actionable and always shows. */}
                    <div className={fallbackHint ? undefined : "rg-hide-m"} style={{ textAlign: "center", color: "var(--muted)", fontSize: 12, marginTop: 6 }}>
                      {fallbackHint ?? "See a true-to-life preview of this door on your home."}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* rg-doors-card only when doors exist: the mobile stylesheet orders it below the picker
              (setup admin), but the zero-doors empty state IS the primary action and stays on top. */}
          <div style={card} className={rows.length > 0 ? "rg-doors-card" : undefined}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Your doors</div>
              <button
                type="button"
                onClick={handleAddDoor}
                data-testid="add-door"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: "var(--radius-pill)", fontSize: 13, fontWeight: 600, cursor: "pointer", border: "1px solid var(--border-strong)", background: "var(--surface)", color: "var(--brand-active)" }}
              >
                <PlusIcon size={15} /> Add door
              </button>
            </div>
            {rows.length > 0 ? (
              <DoorLegend
                rows={rows}
                selectedDoorId={ui.selectedDoorId}
                // The "All doors" entry only makes sense with doors to gang; it's the multi-door
                // default (selected via the ALL_DOORS sentinel in selectedDoorId).
                showAllEntry={doors.length > 1}
                onSelect={(id) => { selectDoor(id); setMode("edit"); }}
                onHover={setHoveredDoorId}
                onRemove={(id) => setPendingRemoveId(id)}
              />
            ) : (
              <div style={{ fontSize: 13, color: "var(--muted)" }}>
                We couldn't automatically find a garage door in this photo. Use{" "}
                <strong style={{ color: "var(--text)" }}>“Add door”</strong> to place one by hand, or{" "}
                <button
                  type="button"
                  onClick={() => navigate(tenantPath(tenantSlug))}
                  style={{ background: "none", border: "none", padding: 0, color: "var(--brand)", font: "inherit", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                >
                  try a clearer photo
                </button>.
              </div>
            )}
          </div>
        </div>

        <div className="rg-visualize-col">
          {/* Size + top shape are properties of each OPENING, not of a catalogue choice, so they show
              whenever a door is being edited — before a collection is even picked (they steer which
              designs/prices apply). DoorGeometryPanel owns the same-for-all vs per-door layout. */}
          <DoorGeometryPanel
            doors={doors}
            allMode={allMode}
            selected={selected}
            // Mobile 1-door sessions start with the geometry collapsed to a summary row — the
            // detected Single/Double + Flat verdict is right for the common case, and the style
            // picker (promoted above it there) is the core loop. One-shot media check at render;
            // the panel only reads it for initial state and re-expands itself when a door is added.
            startCollapsed={doors.length === 1 && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 860px)").matches}
            onSize={setSize}
            onShape={setTopShape}
          />
          {/* Geometry is per-opening; STYLE, by contrast, gangs across every door in ALL mode — say so
              once, right above the picker, since the per-door "Door N ·" labels that used to imply it
              are gone. */}
          {allMode && doors.length > 1 && (
            <div style={{ fontSize: 12, color: "var(--muted)" }}>
              <strong style={{ color: "var(--text)" }}>Style</strong> picks below apply to all {doors.length} doors.
            </div>
          )}
          {catalog.collection ? (
            <DoorPicker
              collection={catalog.collection}
              collections={catalog.collections}
              // ALL mode: pickers highlight only what EVERY door shares — mixed fields show as
              // unselected until the next pick unifies them (see sharedSelection).
              selection={allMode ? sharedSelection(doors) : selected?.selection ?? EMPTY_SELECTION}
              open={ui.openSection}
              // ALL mode offers only options renderable at every distinct door size.
              widthsFt={allMode ? [...new Set(doors.map((d) => sizeDims(effectiveSize(d)).width))] : [sizeDims(selectedSize).width]}
              // Only priced (dealer) mode shows the insulation "+$X"; showroom shows the options price-free.
              showPrices={pricingEnabled}
              onToggleSection={setOpenSection}
              onPickCollection={pickCollection}
              onChange={applySelection}
            />
          ) : (
            <div style={card}>
              {/* Multi-brand catalogues (the /renoglance demo, multi-manufacturer dealers) lead with a
                  manufacturer selector; picking a manufacturer reveals only its collections below.
                  Single-brand catalogues skip straight to the flat collection grid. */}
              {multiBrand && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>
                    Step 1 — Manufacturer
                  </div>
                  <ManufacturerSelect
                    options={brandGroups.map((g) => ({ id: g.id, name: g.name, count: g.collections.length }))}
                    selectedId={activeInitialManuf}
                    onSelect={setInitialManufId}
                    testId="initial-manufacturers"
                  />
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>
                {multiBrand ? "Step 2 — Collection" : "Choose a collection"}
              </div>
              <CollectionTiles collections={shownInitialCollections} onPick={pickCollection} />
              {catalog.collections.length === 0 && <div style={{ color: "var(--muted)" }}>Loading styles…</div>}
            </div>
          )}

          {price && (
            <div style={card}>
              <PriceLine
                price={price}
                isSingle={selectedIsSingle}
                // Use the tenant-overridden collection/door name only — never price.displayName, which
                // carries the un-overridden master collection name (e.g. "Classica") and would leak it.
                title={doorName || catalog.collection?.name || undefined}
                subtext={priceSubtext}
                financingEnabled={branding?.financingEnabled}
                financingTermMonths={branding?.financingTermMonths}
                financingApr={branding?.financingApr}
              />
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 8 }}>
                Estimated installed price — your dealer confirms the final quote.
              </div>
            </div>
          )}

          {leadCaptureEnabled && (
            // Hidden on mobile (rg-quote-inflow) — the sticky bottom quote bar replaces it there.
            <Button variant="primary" block className="rg-quote-inflow" trailingIcon={<ArrowRightIcon />} disabled={!composite} onClick={() => navigate(tenantPath(tenantSlug, "schedule"))}>
              Get a quote
            </Button>
          )}
        </div>
      </div>

      {leadCaptureEnabled && (
        // Mobile-only sticky conversion bar: live price + CTA stay visible for the whole styling
        // session (the grid reserves bottom padding so it never covers the last in-flow content).
        // The detailed in-flow price card (promo, financing terms, disclaimer) remains above.
        <div className="rg-quote-bar" data-testid="quote-bar">
          <div style={{ minWidth: 0, flex: 1, lineHeight: 1.25 }}>
            {price && barPrice ? (
              <>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>Starting from</div>
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {dollars(barPrice.headlineCents)}
                  {branding?.financingEnabled && barPrice.monthlyCents != null && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)" }}> · {dollarsCents(barPrice.monthlyCents)}/mo</span>
                  )}
                </div>
              </>
            ) : (
              // No resolvable price (still picking, showroom mode, or a rule priced only the other
              // size) — show the door identity instead; never "$0".
              <div style={{ fontSize: 13, color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {price ? "Pricing on request" : doorName || "Pick a style below"}
              </div>
            )}
          </div>
          <Button variant="primary" trailingIcon={<ArrowRightIcon />} disabled={!composite} onClick={() => navigate(tenantPath(tenantSlug, "schedule"))}>
            Get a quote
          </Button>
        </div>
      )}

      {/* Fullscreen: in EDIT mode this is an editing surface (full-viewport QuadEditor with
          pinch-zoom — corner-dragging on a phone-sized preview is the product's hardest
          interaction); Before/After keeps the passive full-res lightbox. */}
      {ui.fullscreen && effectiveMode === "edit" && (
        <FullscreenEditor
          photoUrl={doc.photoUrl}
          backdropUrl={display}
          doors={editorDoors}
          activeDoorIds={activeDoorIds}
          busy={busy}
          onSelectDoor={selectDoor}
          onQuadChange={(id, q) => setDoorQuad(id, q)}
          onQuadCommit={(id, q) => { setDoorQuad(id, q); void renderComposite(); }}
          onProfileChange={(id, tp) => setDoorTopProfile(id, tp)}
          onProfileCommit={(id, tp) => { setDoorTopProfile(id, tp); void renderComposite(); }}
          onClose={() => setFullscreen(false)}
        />
      )}
      {/* Compare-mode fullscreen: the SAME Before/After slider, viewport-sized and full-res, with the
          slider position shared with the in-page card (continuity both ways). No scrim-to-close —
          a slider drag released outside the images must never dismiss the view; close is the X (44px)
          or Escape. The slider root sets touch-action: none itself, so drags never scroll the page. */}
      {ui.fullscreen && effectiveMode !== "edit" && display && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,0.94)" }}>
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CompareView
              fullscreen
              before={doc.photoUrl}
              after={display}
              position={ui.comparePosition}
              onPositionChange={setComparePosition}
            />
          </div>
          <button
            type="button"
            title="Close"
            aria-label="Close full screen"
            onClick={() => setFullscreen(false)}
            style={{ position: "absolute", top: 16, right: 16, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.12)", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", zIndex: 7 }}
          >
            <CloseIcon size={22} />
          </button>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingRemoveId}
        title="Remove this door?"
        message="This door and its style selection will be removed from your photo."
        confirmLabel="Remove door"
        onConfirm={confirmRemove}
        onCancel={() => setPendingRemoveId(null)}
      />
    </AppShell>
  );
}

function CollectionTiles({ collections, onPick }: { collections: CatalogCollection[]; onPick: (c: CatalogCollection) => void }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
      {collections.map((c) => (
        <button
          key={c.id}
          type="button"
          className="rg-tile"
          onClick={() => onPick(c)}
          style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", padding: 6, cursor: "pointer", textAlign: "center" }}
        >
          {/* Graceful thumb: a catalogue collection icon that 404s shows a neutral door-glyph
              placeholder, not a broken-image glyph (some manufacturers' preview icons aren't
              materialised yet). The collection name is always shown below. */}
          <Thumb src={c.imageUrl} aspect="4 / 3" fit="cover" />
          <div style={{ fontSize: 12, marginTop: 6 }}>{c.name}</div>
        </button>
      ))}
    </div>
  );
}

function SegButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "7px 14px",
        borderRadius: "var(--radius-pill)",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        border: `1px solid ${active ? "var(--brand)" : "var(--border-strong)"}`,
        background: active ? "var(--brand-tint)" : "var(--surface)",
        color: active ? "var(--brand-active)" : "var(--muted)",
      }}
    >
      {children}
    </button>
  );
}
